/**
 * Queue Store (Application)
 *
 * Module-level state management for the queue feature.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { QueueStatus, QueueTab } from '../../domain/types';

import type { CaseId, CaseSummary, QueueStore } from '../../domain/types';

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  status: QueueStatus.Stopped,
  currentCaseId: null as CaseId | null,
  cases: [] as CaseSummary[],
  completedCount: 0,
  activeTab: QueueTab.Queue,
};

// ============================================================================
// Store
// ============================================================================

export const useQueueStore = create<QueueStore>()(
  devtools(
    immer((set) => ({
      ...initialState,

      startQueue: () =>
        set((state) => {
          state.status = QueueStatus.Running;
          if (!state.currentCaseId && state.cases.length > 0) {
            state.currentCaseId = state.cases[0].id;
          }
        }),

      pauseQueue: () =>
        set((state) => {
          if (state.status === QueueStatus.Running) {
            state.status = QueueStatus.Paused;
          }
        }),

      stopQueue: () =>
        set((state) => {
          state.status = QueueStatus.Stopped;
        }),

      setCurrentCase: (caseId) =>
        set((state) => {
          state.currentCaseId = caseId;
        }),

      nextCase: () =>
        set((state) => {
          const currentIndex = state.cases.findIndex(
            (c) => c.id === state.currentCaseId
          );
          if (currentIndex < state.cases.length - 1) {
            state.currentCaseId = state.cases[currentIndex + 1].id;
            state.completedCount += 1;
          } else {
            state.currentCaseId = null;
            state.status = QueueStatus.Stopped;
          }
        }),

      setCases: (cases) =>
        set((state) => {
          state.cases = cases;
        }),

      setActiveTab: (tab) =>
        set((state) => {
          state.activeTab = tab;
        }),
    })),
    { name: 'queue-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const useCurrentCase = () =>
  useQueueStore((state) =>
    state.cases.find((c) => c.id === state.currentCaseId) ?? null
  );

export const useQueueStatus = () =>
  useQueueStore((state) => ({
    status: state.status,
    isRunning: state.status === QueueStatus.Running,
    isPaused: state.status === QueueStatus.Paused,
    isStopped: state.status === QueueStatus.Stopped,
  }));

export const useQueueStats = () =>
  useQueueStore((state) => ({
    total: state.cases.length,
    completed: state.completedCount,
    remaining: state.cases.length - state.completedCount,
  }));


